<template>
  <dashboard-layout title="Twin Figures" subtitle="The celebrity & historical pool the twins report draws from">
    <template #actions>
      <router-link to="/celebrity-twins" class="link-btn">← Reports</router-link>
      <search-box v-model="search" placeholder="Search name / category…" />
      <button class="refresh-btn" @click="load" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <div class="tabs">
      <button :class="['tab-btn', { active: tab === 'figures' }]" @click="tab = 'figures'">Figures</button>
      <button :class="['tab-btn', { active: tab === 'credits' }]" @click="tab = 'credits'">Photo credits</button>
    </div>

    <loading-state v-if="loading && !figures.length" label="Loading figure pool…" />

    <template v-else>
      <!-- Overview -->
      <stat-grid>
        <stat-card label="Figures" :value="figures.length.toLocaleString('en-US')" icon="users" accent>
          <template #sub>{{ activeCount }} active · {{ draftCount }} draft</template>
        </stat-card>
        <stat-card label="Birth time known" :value="exactCount.toLocaleString('en-US')" icon="activity">
          <template #sub>{{ pct(exactCount, figures.length) }}% exact · rest approximate</template>
        </stat-card>
        <stat-card label="Attribution needed" :value="attribCount.toLocaleString('en-US')" icon="filetext">
          <template #sub>{{ flaggedCount }} licence-flagged</template>
        </stat-card>
        <stat-card label="Ever matched" :value="matchedDistinct.toLocaleString('en-US')" icon="smile">
          <template #sub>{{ neverMatchedActive }} active never picked</template>
        </stat-card>
        <stat-card label="Sun signs ≥ 25" :value="`${signsAtTarget}/12`" icon="sliders">
          <template #sub>active figures per Sun sign</template>
        </stat-card>
      </stat-grid>

      <template v-if="tab === 'figures'">
        <!-- Distributions -->
        <div class="dual-grid">
          <data-panel title="Active figures by Sun sign (target ≥ 25)">
            <div class="dist-bars">
              <div v-for="s in sunSignDist" :key="s.label" class="dist-row">
                <span class="dist-label">{{ s.label }}</span>
                <div class="dist-track"><div class="dist-fill" :class="{ under: s.count < 25 }" :style="{ width: s.pct + '%' }"></div></div>
                <span class="dist-val" :class="{ under: s.count < 25 }">{{ s.count }}</span>
              </div>
            </div>
          </data-panel>
          <div class="stack">
            <data-panel title="By era">
              <div class="dist-bars">
                <div v-for="s in eraDist" :key="s.label" class="dist-row">
                  <span class="dist-label wide">{{ s.label }}</span>
                  <div class="dist-track"><div class="dist-fill" :style="{ width: s.pct + '%' }"></div></div>
                  <span class="dist-val">{{ s.count }}</span>
                </div>
              </div>
            </data-panel>
            <data-panel title="By category">
              <div class="dist-bars cat-bars">
                <div v-for="s in categoryDist" :key="s.label" class="dist-row">
                  <span class="dist-label wide" :title="s.label">{{ s.label }}</span>
                  <div class="dist-track"><div class="dist-fill" :style="{ width: s.pct + '%' }"></div></div>
                  <span class="dist-val">{{ s.count }}</span>
                </div>
              </div>
            </data-panel>
          </div>
        </div>

        <!-- Filters -->
        <div class="filter-rows">
          <div class="filter-line"><span class="fl-label">Era</span><filter-chips v-model="eraFilter" :options="eraOptions" /></div>
          <div class="filter-line">
            <span class="fl-label">Category</span>
            <select v-model="categoryFilter" class="fig-select">
              <option value="all">All categories ({{ figures.length }})</option>
              <option v-for="c in categoryList" :key="c.value" :value="c.value">{{ c.label }} ({{ c.count }})</option>
            </select>
            <span class="fl-label fl-inline">Sun sign</span>
            <select v-model="sunFilter" class="fig-select">
              <option value="all">All signs</option>
              <option v-for="s in sunList" :key="s.value" :value="s.value">{{ s.label }} ({{ s.count }})</option>
            </select>
          </div>
          <div class="filter-line"><span class="fl-label">Time</span><filter-chips v-model="timeFilter" :options="timeOptions" /></div>
          <div class="filter-line"><span class="fl-label">Status</span><filter-chips v-model="statusFilter" :options="statusOptions" /></div>
          <div class="filter-line"><span class="fl-label">View</span><filter-chips v-model="viewFilter" :options="viewOptions" /></div>
          <div class="filter-line"><span class="fl-label">Sort</span><filter-chips v-model="sortBy" :options="sortOptions" /></div>
        </div>

        <!-- Figures table -->
        <data-panel flush>
          <template #title>Figures <span class="muted">({{ filteredFigures.length.toLocaleString('en-US') }})</span></template>
          <data-table :columns="figureColumns" :rows="filteredFigures" row-key="id">
            <template #cell-photo="{ row }">
              <div class="fig-photo">
                <img v-if="row.photo && row.photo.url" :src="row.photo.url" :alt="row.name" loading="lazy" referrerpolicy="no-referrer" @error="onImgError">
                <div v-else class="fig-nophoto">{{ (row.name || '?').slice(0, 1) }}</div>
              </div>
            </template>
            <template #cell-name="{ row }">
              <div class="fig-name">{{ row.name || '—' }}</div>
              <div class="fig-desc" :title="row.description">{{ truncate(row.description, 40) }}</div>
            </template>
            <template #cell-era="{ row }"><span class="era-tag">{{ row.era || '—' }}</span></template>
            <template #cell-category="{ row }">{{ row.category || '—' }}</template>
            <template #cell-born="{ row }">
              <div class="born-date">{{ row.born || '—' }}</div>
              <div class="born-place" :title="row.birthPlace">{{ truncate(row.birthPlace, 24) || '—' }}</div>
            </template>
            <template #cell-sun="{ row }">{{ row.sun || '—' }}</template>
            <template #cell-moon="{ row }">{{ row.moon || '—' }}</template>
            <template #cell-rising="{ row }">{{ row.rising || '—' }}</template>
            <template #cell-time="{ row }">
              <span :title="row.timeSource ? ('source: ' + row.timeSource) : ''">
                <status-badge :variant="row.timeAccuracy === 'exact' ? 'success' : 'neutral'" :label="row.birthTime ? (row.timeAccuracy || 'known') : (row.timeAccuracy || '—')" />
              </span>
            </template>
            <template #cell-licence="{ row }">
              <status-badge :variant="licenceInfo(row).variant" :label="licenceInfo(row).short" :title="row.photo ? row.photo.license : ''" />
            </template>
            <template #cell-status="{ row }">
              <status-badge :variant="row.status === 'active' ? 'success' : 'warning'" :label="row.status || '—'" />
            </template>
            <template #cell-matched="{ row }">
              <span :class="matchedCount(row.id) ? 'strong' : 'never'">{{ matchedCount(row.id) }}</span>
            </template>
            <template #empty>No figures match your filters.</template>
          </data-table>
        </data-panel>
      </template>

      <!-- Photo credits -->
      <template v-else>
        <p class="note">
          Portraits that require attribution in the app (CC BY / CC BY-SA, OGL, GODL, FAL, Attribution), plus any with a
          non-free or unrecognised licence (<span class="flag-text">flagged</span>). {{ creditRows.length.toLocaleString('en-US') }} figures with a photo.
        </p>
        <div class="filters">
          <button class="link-btn" @click="exportCredits">Download CSV</button>
        </div>
        <data-panel flush>
          <template #title>Credits <span class="muted">({{ creditRows.length.toLocaleString('en-US') }})</span></template>
          <data-table :columns="creditColumns" :rows="creditRows" row-key="id">
            <template #cell-name="{ row }"><span class="fig-name">{{ row.name }}</span></template>
            <template #cell-artist="{ row }"><span :title="row.artist">{{ row.artist || '—' }}</span></template>
            <template #cell-licence="{ row }">
              <status-badge :variant="licenceInfo(row).variant" :label="row.license || '—'" />
              <span v-if="licenceInfo(row).attrib" class="attrib-note">attribution</span>
            </template>
            <template #cell-source="{ row }">
              <a v-if="row.source" :href="row.source" target="_blank" rel="noreferrer" class="user-link" :title="row.source">{{ truncate(sourceHost(row.source), 36) }}</a>
              <span v-else class="muted">—</span>
            </template>
            <template #empty>No photo credits found.</template>
          </data-table>
        </data-panel>
      </template>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { FirebaseCelebrityTwinsReportService } from '../services/firebaseCelebrityTwinsReportService'
import { classifyLicence } from '../services/licence'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const ZODIAC = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']
const SUN_TARGET = 25

const figureColumns = [
  { key: 'photo', label: '', width: '52px' },
  { key: 'name', label: 'Name' },
  { key: 'era', label: 'Era', width: '110px' },
  { key: 'category', label: 'Category', width: '110px' },
  { key: 'born', label: 'Born', width: '160px' },
  { key: 'sun', label: 'Sun', width: '80px' },
  { key: 'moon', label: 'Moon', width: '80px' },
  { key: 'rising', label: 'Rising', width: '80px' },
  { key: 'time', label: 'Time', width: '110px' },
  { key: 'licence', label: 'Licence', width: '110px' },
  { key: 'status', label: 'Status', width: '90px' },
  { key: 'matched', label: 'Matched', width: '80px' }
]
const creditColumns = [
  { key: 'name', label: 'Name', width: '200px' },
  { key: 'artist', label: 'Artist' },
  { key: 'licence', label: 'Licence', width: '200px' },
  { key: 'source', label: 'Source' }
]

const normSign = (s) => {
  if (!s) return ''
  const t = String(s).trim()
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()
}

export default {
  name: 'TwinFiguresDashboard',
  components: { DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge },
  setup() {
    const service = new FirebaseCelebrityTwinsReportService()

    const figures = ref([])
    const matchTally = ref({})
    const loading = ref(true)
    const error = ref('')
    const tab = ref('figures')
    const search = ref('')
    const eraFilter = ref('all')
    const categoryFilter = ref('all')
    const sunFilter = ref('all')
    const timeFilter = ref('all')
    const statusFilter = ref('all')
    const viewFilter = ref('all')
    const sortBy = ref('name')

    const load = async () => {
      loading.value = true
      error.value = ''
      try {
        const [figs, gens] = await Promise.all([service.getCelebrities(), service.getGenerations()])
        figures.value = figs
        // Tally how often each figure id was picked across all generation runs.
        const tally = {}
        gens.forEach(g => (g.matches || []).forEach(m => { if (m.id) tally[m.id] = (tally[m.id] || 0) + 1 }))
        matchTally.value = tally
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied the read (not signed in). Reload and try again.'
        else error.value = (e && e.message) || 'Failed to load figures.'
      } finally {
        loading.value = false
      }
    }

    const matchedCount = (id) => matchTally.value[id] || 0
    const licenceInfo = (row) => classifyLicence(row.photo ? row.photo.license : '')

    const activeFigures = computed(() => figures.value.filter(f => f.status === 'active'))
    const activeCount = computed(() => activeFigures.value.length)
    const draftCount = computed(() => figures.value.filter(f => f.status === 'draft').length)
    const exactCount = computed(() => figures.value.filter(f => f.timeAccuracy === 'exact').length)
    const attribCount = computed(() => figures.value.filter(f => f.photo && classifyLicence(f.photo.license).attrib).length)
    const flaggedCount = computed(() => figures.value.filter(f => f.photo && classifyLicence(f.photo.license).flag).length)
    const matchedDistinct = computed(() => Object.keys(matchTally.value).filter(id => matchTally.value[id] > 0).length)
    const neverMatchedActive = computed(() => activeFigures.value.filter(f => !matchTally.value[f.id]).length)

    const sunSignDist = computed(() => {
      const counts = {}
      ZODIAC.forEach(z => { counts[z] = 0 })
      let other = 0
      activeFigures.value.forEach(f => {
        const sign = normSign(f.sun)
        if (counts[sign] != null) counts[sign]++
        else if (sign) other++
      })
      const max = Math.max(SUN_TARGET, ...ZODIAC.map(z => counts[z]), 1)
      const rows = ZODIAC.map(z => ({ label: z, count: counts[z], pct: Math.round((counts[z] / max) * 100) }))
      if (other) rows.push({ label: 'Other', count: other, pct: Math.round((other / max) * 100) })
      return rows
    })
    const signsAtTarget = computed(() => ZODIAC.filter(z => sunSignDist.value.find(r => r.label === z && r.count >= SUN_TARGET)).length)

    const distOf = (key) => {
      const counts = {}
      figures.value.forEach(f => { const v = f[key] || '—'; counts[v] = (counts[v] || 0) + 1 })
      const max = Math.max(...Object.values(counts), 1)
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([label, count]) => ({ label, count, pct: Math.round((count / max) * 100) }))
    }
    const eraDist = computed(() => distOf('era'))
    const categoryDist = computed(() => distOf('category'))

    const eraOptions = computed(() => ([
      { value: 'all', label: 'All', count: figures.value.length },
      { value: 'historical', label: 'Historical', count: figures.value.filter(f => f.era === 'historical').length },
      { value: 'contemporary', label: 'Contemporary', count: figures.value.filter(f => f.era === 'contemporary').length }
    ]))
    const timeOptions = computed(() => ([
      { value: 'all', label: 'All', count: figures.value.length },
      { value: 'exact', label: 'Exact', count: exactCount.value },
      { value: 'approximate', label: 'Approx', count: figures.value.filter(f => f.timeAccuracy === 'approximate').length }
    ]))
    const statusOptions = computed(() => ([
      { value: 'all', label: 'All', count: figures.value.length },
      { value: 'active', label: 'Active', count: activeCount.value },
      { value: 'draft', label: 'Draft', count: draftCount.value }
    ]))
    const viewOptions = computed(() => ([
      { value: 'all', label: 'All', count: figures.value.length },
      { value: 'never', label: 'Never matched', count: figures.value.filter(f => !matchTally.value[f.id]).length },
      { value: 'flagged', label: 'Licence flagged', count: flaggedCount.value }
    ]))
    const sortOptions = [
      { value: 'name', label: 'Name' },
      { value: 'matched', label: 'Most matched' },
      { value: 'sitelinks', label: 'Notability' }
    ]

    // Category dropdown — distinct values from the pool, most common first.
    const categoryList = computed(() => {
      const counts = {}
      figures.value.forEach(f => { if (f.category) counts[f.category] = (counts[f.category] || 0) + 1 })
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([value, count]) => ({ value, label: value, count }))
    })
    // Sun-sign dropdown — zodiac order, with the active-pool count per sign.
    const sunList = computed(() => {
      const counts = {}
      activeFigures.value.forEach(f => { const s = normSign(f.sun); if (s) counts[s] = (counts[s] || 0) + 1 })
      const ordered = ZODIAC.filter(z => counts[z]).map(z => ({ value: z, label: z, count: counts[z] }))
      Object.keys(counts).filter(s => !ZODIAC.includes(s)).sort().forEach(s => ordered.push({ value: s, label: s, count: counts[s] }))
      return ordered
    })

    const filteredFigures = computed(() => {
      const q = search.value.toLowerCase().trim()
      let rows = figures.value.filter(f => {
        if (eraFilter.value !== 'all' && f.era !== eraFilter.value) return false
        if (categoryFilter.value !== 'all' && f.category !== categoryFilter.value) return false
        if (sunFilter.value !== 'all' && normSign(f.sun) !== sunFilter.value) return false
        if (timeFilter.value !== 'all' && f.timeAccuracy !== timeFilter.value) return false
        if (statusFilter.value !== 'all' && f.status !== statusFilter.value) return false
        if (viewFilter.value === 'never' && matchTally.value[f.id]) return false
        if (viewFilter.value === 'flagged' && !(f.photo && classifyLicence(f.photo.license).flag)) return false
        if (q) {
          const hay = `${f.name} ${f.category} ${f.description} ${f.sun} ${f.birthPlace} ${(f.occupations || []).join(' ')}`.toLowerCase()
          if (!hay.includes(q)) return false
        }
        return true
      })
      rows = rows.slice()
      if (sortBy.value === 'matched') rows.sort((a, b) => matchedCount(b.id) - matchedCount(a.id) || a.name.localeCompare(b.name))
      else if (sortBy.value === 'sitelinks') rows.sort((a, b) => b.sitelinks - a.sitelinks || a.name.localeCompare(b.name))
      else rows.sort((a, b) => a.name.localeCompare(b.name))
      return rows
    })

    const creditRows = computed(() =>
      figures.value
        .filter(f => f.photo && f.photo.url)
        .map(f => ({ id: f.id, name: f.name, artist: f.photo.artist || '', license: f.photo.license || '', source: f.photo.source || '' }))
        .sort((a, b) => a.name.localeCompare(b.name))
    )

    const sourceHost = (url) => { try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url } }

    const exportCredits = () => {
      const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`
      const header = ['Name', 'Artist', 'License', 'Attribution required', 'Source']
      const lines = [header.map(esc).join(',')]
      creditRows.value.forEach(r => {
        const info = classifyLicence(r.license)
        lines.push([r.name, r.artist, r.license, info.attrib ? 'yes' : 'no', r.source].map(esc).join(','))
      })
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `twin-figure-credits-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }

    const pct = (n, total) => total ? Math.round((n / total) * 100) : 0
    const truncate = (t, max) => !t ? '' : (t.length > max ? t.slice(0, max) + '…' : t)
    const onImgError = (e) => { const el = e.target; el.style.display = 'none'; if (el.parentElement) el.parentElement.classList.add('img-failed') }

    onMounted(load)

    return {
      figures, loading, error, tab, search,
      eraFilter, categoryFilter, sunFilter, timeFilter, statusFilter, viewFilter, sortBy,
      eraOptions, timeOptions, statusOptions, viewOptions, sortOptions, categoryList, sunList,
      figureColumns, creditColumns,
      activeCount, draftCount, exactCount, attribCount, flaggedCount, matchedDistinct, neverMatchedActive, signsAtTarget,
      sunSignDist, eraDist, categoryDist, filteredFigures, creditRows,
      load, matchedCount, licenceInfo, exportCredits, sourceHost, pct, truncate, onImgError
    }
  }
}
</script>

<style scoped>
.error-banner { background: rgba(248, 191, 113, 0.12); border: 1px solid rgba(248, 191, 113, 0.35); color: var(--warning); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.refresh-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--text-primary); font-size: 13px; cursor: pointer; }
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }
.link-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--primary-color); font-size: 13px; text-decoration: none; white-space: nowrap; cursor: pointer; }
.link-btn:hover { background: var(--hover-bg); }

.tabs { display: flex; gap: 0; background: rgba(255, 255, 255, 0.05); border-radius: 8px; overflow: hidden; border: 1px solid var(--panel-border); width: fit-content; }
.tab-btn { border: none; border-radius: 0; padding: 8px 16px; background: transparent; color: var(--text-primary); font-size: 13px; cursor: pointer; }
.tab-btn:not(:last-child) { border-right: 1px solid var(--panel-border); }
.tab-btn:hover { background: rgba(255, 255, 255, 0.08); }
.tab-btn.active { background: linear-gradient(135deg, var(--primary-color), var(--gradient-2)); }

.note { margin: 0; font-size: 12px; color: var(--text-secondary); }
.flag-text { color: var(--danger); }

.dual-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; }
.stack { display: flex; flex-direction: column; gap: 16px; }

.dist-bars { display: flex; flex-direction: column; gap: 7px; }
.cat-bars { max-height: 260px; overflow-y: auto; }
.dist-row { display: flex; align-items: center; gap: 10px; }
.dist-label { width: 90px; flex-shrink: 0; font-size: 12px; color: var(--text-primary); }
.dist-label.wide { width: 130px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dist-track { flex: 1; height: 8px; background: rgba(255, 255, 255, 0.06); border-radius: 4px; overflow: hidden; }
.dist-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg, var(--primary-color), var(--gradient-2)); }
.dist-fill.under { background: rgba(248, 191, 113, 0.7); }
.dist-val { width: 34px; text-align: right; font-size: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums; }
.dist-val.under { color: var(--warning); font-weight: 600; }

.filter-rows { display: flex; flex-direction: column; gap: 8px; }
.filter-line { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fl-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); width: 48px; flex-shrink: 0; }
.fl-label.fl-inline { width: auto; margin-left: 14px; }
.fig-select { padding: 7px 10px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: 8px; color: var(--text-primary); font-size: 12.5px; cursor: pointer; max-width: 220px; }
.filters { display: flex; gap: 12px; flex-wrap: wrap; }

.born-date { font-size: 12.5px; color: var(--text-primary); font-variant-numeric: tabular-nums; }
.born-place { font-size: 11px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 150px; }

.muted { color: var(--text-secondary); font-weight: 400; }
.strong { font-weight: 600; }
.never { color: var(--text-secondary); opacity: 0.5; }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
.era-tag { font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary); }
.attrib-note { font-size: 10px; color: var(--text-secondary); margin-left: 6px; }

.fig-photo { width: 40px; height: 40px; border-radius: 8px; overflow: hidden; background: var(--panel-bg); border: 1px solid var(--panel-border); }
.fig-photo img { width: 100%; height: 100%; object-fit: cover; }
.fig-nophoto { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; color: var(--text-secondary); }
.fig-name { font-weight: 600; font-size: 13px; color: var(--text-primary); }
.fig-desc { font-size: 11px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px; }
</style>
